import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Star, Loader2, CheckCircle2, ShieldAlert, ExternalLink } from "lucide-react";
import { getReviewByToken, submitCustomerReview, submitPrivateFeedback, recordReviewEvent } from "@/lib/reviews/public.functions";

export const Route = createFileRoute("/review/$token")({
  head: () => ({ meta: [{ title: "Rate your experience — Michael's Painting" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: ReviewPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="min-h-screen grid place-items-center bg-[#030303] text-zinc-300 px-6 text-center">
        <div>
          <ShieldAlert className="mx-auto size-8 text-amber-300 mb-3" />
          <p className="text-red-400 mb-3 text-sm">{error.message}</p>
          <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
        </div>
      </div>
    );
  },
  notFoundComponent: () => <div className="min-h-screen grid place-items-center bg-[#030303] text-zinc-400">Link not found.</div>,
});

type Step = "rate" | "five" | "four" | "low" | "thanks";

function ReviewPage() {
  const { token } = Route.useParams();
  const fetchFn = useServerFn(getReviewByToken);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["review", token],
    queryFn: () => fetchFn({ data: { token } }),
    staleTime: 30_000,
  });

  const [rating, setRating] = useState<number | null>(null);
  const [hover, setHover] = useState<number>(0);
  const [step, setStep] = useState<Step>("rate");
  const [busy, setBusy] = useState(false);

  const submitRating = useServerFn(submitCustomerReview);
  const submitFeedback = useServerFn(submitPrivateFeedback);
  const recordEvent = useServerFn(recordReviewEvent);

  if (isLoading) {
    return <div className="min-h-screen bg-[#030303] grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;
  }
  if (!data) {
    return <div className="min-h-screen bg-[#030303] text-zinc-300 grid place-items-center px-6 text-center">This review link is invalid or has expired.</div>;
  }

  const company = (data as any).company_name as string;
  const alreadySubmitted = (data as any).already_submitted as boolean;
  const submittedRating = (data as any).submitted_rating as number | null;

  if (alreadySubmitted) {
    return (
      <div className="min-h-screen bg-[#030303] text-zinc-200 grid place-items-center px-6">
        <div className="max-w-md text-center border border-white/10 rounded-3xl bg-white/[0.03] p-10">
          <CheckCircle2 className="mx-auto size-10 text-amber-300 mb-4" />
          <h1 className="font-serif text-2xl text-white mb-2">Thank you!</h1>
          <p className="text-sm text-zinc-400 mb-1">We've already received your {submittedRating}★ rating.</p>
          <p className="text-xs text-zinc-500">Submitted {new Date((data as any).submitted_at).toLocaleDateString()}.</p>
          <Link to="/" className="mt-6 inline-block text-amber-300 text-sm underline">Back to {company}</Link>
        </div>
      </div>
    );
  }

  async function pickRating(r: number) {
    setRating(r);
    setBusy(true);
    try {
      await submitRating({ data: { token, rating: r, publicConsent: r >= 4, userAgent: navigator.userAgent } });
      if (r === 5) setStep("five");
      else if (r === 4) setStep("four");
      else setStep("low");
    } catch (e) {
      alert((e as Error).message);
      setRating(null);
    } finally { setBusy(false); }
  }

  async function logClick(kind: "google_click" | "facebook_click") {
    try { await recordEvent({ data: { token, eventType: kind } }); } catch {/* noop */}
  }

  return (
    <div className="min-h-screen bg-[#030303] text-zinc-200 flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-xl">
        <div className="text-center mb-8">
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-semibold">{company}</p>
          <h1 className="font-serif text-3xl md:text-4xl mt-2 text-white">How did we do?</h1>
        </div>

        {step === "rate" && (
          <div className="border border-white/10 rounded-3xl bg-white/[0.03] p-8 text-center">
            <p className="text-zinc-400 mb-6">Tap to rate your experience</p>
            <div className="flex justify-center gap-2 mb-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  disabled={busy}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => pickRating(n)}
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                  className="p-2 transition-transform hover:scale-110 disabled:opacity-50"
                >
                  <Star className={`size-12 md:size-14 ${(hover || rating || 0) >= n ? "fill-amber-300 text-amber-300" : "text-zinc-700"}`} />
                </button>
              ))}
            </div>
            {busy && <Loader2 className="mx-auto mt-4 size-5 animate-spin text-amber-300" />}
          </div>
        )}

        {step === "five" && <FiveStar data={data as any} onGoogle={() => logClick("google_click")} onFacebook={() => logClick("facebook_click")} token={token} />}
        {step === "four" && <FourStar data={data as any} token={token} onGoogle={() => logClick("google_click")} onFacebook={() => logClick("facebook_click")} onContinue={() => setStep("thanks")} />}
        {step === "low" && <LowStar rating={rating!} token={token} onSubmit={async (payload) => { await submitFeedback({ data: { token, rating: rating!, ...payload } }); setStep("thanks"); }} />}
        {step === "thanks" && (
          <div className="border border-white/10 rounded-3xl bg-white/[0.03] p-10 text-center">
            <CheckCircle2 className="mx-auto size-10 text-amber-300 mb-4" />
            <h2 className="font-serif text-2xl text-white mb-2">Thank you for your feedback.</h2>
            <p className="text-zinc-400 text-sm">A member of our team will personally follow up shortly.</p>
          </div>
        )}

        <p className="text-center text-[11px] text-zinc-400 mt-6">Your feedback helps us improve. {rating && rating <= 3 ? "We never publish private feedback." : ""}</p>
      </div>
    </div>
  );
}

function FiveStar({ data, onGoogle, onFacebook, token }: { data: any; onGoogle: () => void; onFacebook: () => void; token: string }) {
  return (
    <div className="border border-amber-300/30 rounded-3xl bg-gradient-to-br from-amber-300/[0.06] to-white/[0.03] p-8 text-center">
      <div className="flex justify-center mb-3">{[1,2,3,4,5].map(n => <Star key={n} className="size-7 fill-amber-300 text-amber-300" />)}</div>
      <h2 className="font-serif text-2xl text-white mb-2">We're thrilled!</h2>
      <p className="text-zinc-300 mb-6">Would you mind sharing your review publicly? It takes 30 seconds and means the world to a local business.</p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        {data.google_url && (
          <a href={data.google_url} target="_blank" rel="noopener noreferrer" onClick={onGoogle}
             className="inline-flex items-center justify-center gap-2 bg-amber-300 text-zinc-950 font-semibold px-6 py-3 rounded-full hover:bg-amber-200">
            ⭐ Leave a Google Review <ExternalLink className="size-4" />
          </a>
        )}
        {data.facebook_url && (
          <a href={data.facebook_url} target="_blank" rel="noopener noreferrer" onClick={onFacebook}
             className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-6 py-3 rounded-full hover:bg-white/5">
            Share on Facebook <ExternalLink className="size-4" />
          </a>
        )}
      </div>
      <Link to="/review/$token/thanks" params={{ token }} className="mt-6 inline-block text-xs text-zinc-500 underline">Skip — continue</Link>
    </div>
  );
}

function FourStar({ data, token, onGoogle, onFacebook, onContinue }: { data: any; token: string; onGoogle: () => void; onFacebook: () => void; onContinue: () => void }) {
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = useServerFn(submitPrivateFeedback);
  return (
    <div className="border border-white/10 rounded-3xl bg-white/[0.03] p-8">
      <h2 className="font-serif text-2xl text-white mb-2 text-center">Glad you had a great experience!</h2>
      <p className="text-zinc-400 text-sm mb-5 text-center">What could we have done to earn a fifth star? (optional)</p>
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
        className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-[16px] text-white focus:border-amber-300/50 outline-none mb-5" placeholder="Your feedback…" />
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        {data.google_url && (
          <a href={data.google_url} target="_blank" rel="noopener noreferrer"
             onClick={async () => {
               if (comment.trim()) { setSaving(true); try { await submit({ data: { token, rating: 4, what_could_improve: comment } }); } finally { setSaving(false); } }
               onGoogle();
             }}
             className="inline-flex items-center justify-center gap-2 bg-amber-300 text-zinc-950 font-semibold px-6 py-3 rounded-full hover:bg-amber-200">
            {saving && <Loader2 className="size-4 animate-spin" />} ⭐ Continue to Google
          </a>
        )}
        {data.facebook_url && (
          <a href={data.facebook_url} target="_blank" rel="noopener noreferrer"
             onClick={async () => { if (comment.trim()) try { await submit({ data: { token, rating: 4, what_could_improve: comment } }); } catch {} onFacebook(); }}
             className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-6 py-3 rounded-full hover:bg-white/5">
            Share on Facebook
          </a>
        )}
      </div>
      <button onClick={async () => { if (comment.trim()) try { await submit({ data: { token, rating: 4, what_could_improve: comment } }); } catch {} onContinue(); }}
        className="mt-5 mx-auto block text-xs text-zinc-500 underline">Skip and finish</button>
    </div>
  );
}

type LowPayload = { comments?: string; phone?: string; preferred_contact?: "phone" | "email" | "text"; best_time_to_call?: string; urgency?: "low" | "normal" | "high" | "urgent" };
function LowStar({ rating, token: _token, onSubmit }: { rating: number; token: string; onSubmit: (p: LowPayload) => Promise<void> }) {
  const [p, setP] = useState<LowPayload>({ urgency: rating <= 2 ? "high" : "normal", preferred_contact: "phone" });
  const [busy, setBusy] = useState(false);
  const urgent = rating <= 2;
  return (
    <div className="border border-white/10 rounded-3xl bg-white/[0.03] p-8">
      <h2 className="font-serif text-2xl text-white mb-2">{urgent ? "We're sorry your experience wasn't what we hoped." : "Thank you for your feedback."}</h2>
      <p className="text-zinc-400 text-sm mb-5">
        {urgent
          ? "We'd appreciate the opportunity to make things right. Please tell us what happened — a real person will follow up directly."
          : "We'd really like to improve. What prevented this from being a five-star experience?"}
      </p>
      <form
        onSubmit={async (e) => { e.preventDefault(); setBusy(true); try { await onSubmit(p); } finally { setBusy(false); } }}
        className="space-y-4"
      >
        <textarea required minLength={5} rows={4} placeholder="Tell us what happened…" value={p.comments ?? ""}
          onChange={(e) => setP({ ...p, comments: e.target.value })}
          className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-[16px] text-white focus:border-amber-300/50 outline-none" />
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="Phone (optional)" value={p.phone ?? ""} onChange={(e) => setP({ ...p, phone: e.target.value })}
            className="bg-black/40 border border-white/10 rounded-xl p-3 text-[16px] text-white focus:border-amber-300/50 outline-none" />
          <select value={p.preferred_contact} onChange={(e) => setP({ ...p, preferred_contact: e.target.value as any })}
            className="bg-black/40 border border-white/10 rounded-xl p-3 text-[16px] text-white focus:border-amber-300/50 outline-none">
            <option value="phone">Call me</option>
            <option value="email">Email me</option>
            <option value="text">Text me</option>
          </select>
        </div>
        <input placeholder="Best time to reach you (optional)" value={p.best_time_to_call ?? ""} onChange={(e) => setP({ ...p, best_time_to_call: e.target.value })}
          className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-[16px] text-white focus:border-amber-300/50 outline-none" />
        {urgent && (
          <select value={p.urgency} onChange={(e) => setP({ ...p, urgency: e.target.value as any })}
            className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-[16px] text-white focus:border-amber-300/50 outline-none">
            <option value="low">Not urgent</option>
            <option value="normal">Within a week</option>
            <option value="high">This week</option>
            <option value="urgent">As soon as possible</option>
          </select>
        )}
        <button type="submit" disabled={busy}
          className="w-full bg-amber-300 text-zinc-950 font-semibold py-3 rounded-full hover:bg-amber-200 disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {busy && <Loader2 className="size-4 animate-spin" />} Send feedback privately
        </button>
        <p className="text-[11px] text-zinc-500 text-center">Your feedback goes directly to ownership. It will not be posted publicly.</p>
      </form>
    </div>
  );
}
